import './index-pagination.scss';
import Base from '../base/base';

/**
 * Configuration options for IndexPagination
 */
interface IndexPaginationOptions {
  /** Selector for the (initially hidden) wrapper around the collection */
  wrapSelector?: string;
  /** Selector for the paginated items, scoped to the wrapper */
  itemSelector?: string;
  /** Items matching this selector are ignored (e.g. Webflow ghost/spacer items) */
  excludeSelector?: string;
  /** Selector for the container the pagination controls render into */
  paginationSelector?: string;
  /** Number of items shown per page, default 6 */
  pageSize?: number;
  /** Class removed from the wrapper once the first page is ready */
  revealClass?: string;
  /** Element scrolled back into view on page change (falls back to the wrapper) */
  scrollTargetSelector?: string;
  /** Scroll back to the top of the collection on page change, default true */
  scrollOnPageChange?: boolean;
  /** Enable debug logging */
  debug?: boolean;
  [key: string]: any;
}

/**
 * IndexPagination
 *
 * Client-side pagination for a server-rendered (Webflow CMS) collection list.
 * All items stay in the DOM; items outside the current page are hidden with a
 * class. The wrapper is expected to ship hidden (e.g. Webflow's `hidden`
 * class) and is revealed only after the first page is applied, so the user
 * never sees the full unpaginated list.
 *
 * Controls (`Prev`, numbered pages, `Next`) are rendered into the container
 * matched by `paginationSelector`; if that container is missing it is created
 * directly after the wrapper. With one page or less, no controls render.
 *
 * Expected DOM structure (listings page defaults):
 * ```html
 * <div class="home-collection-wrap hidden w-dyn-list">
 *   <div class="market-listings w-dyn-items">
 *     <div class="ml-item w-dyn-item">…</div>
 *     …
 *   </div>
 * </div>
 * <div class="listings-pagination"></div>
 * ```
 */
export default class IndexPagination extends Base {
  /** Class applied to items hidden by the active page filter */
  static readonly HIDDEN_ITEM_CLASS = 'cp-item-hidden';
  /** Class applied to the controls container while controls are rendered */
  static readonly CONTAINER_CLASS = 'cp';

  private config: Required<Omit<IndexPaginationOptions, 'debug'>>;

  // DOM references
  private wrap: HTMLElement | null = null;
  private items: HTMLElement[] = [];
  private paginationEl: HTMLElement | null = null;
  private navEl: HTMLElement | null = null;
  private prevButton: HTMLButtonElement | null = null;
  private nextButton: HTMLButtonElement | null = null;
  private pageButtons: HTMLButtonElement[] = [];

  // State
  private currentPage = 1;
  private totalPages = 1;
  private clickHandler: ((event: Event) => void) | null = null;

  constructor(options: IndexPaginationOptions = {}) {
    super(options.debug || false);

    this.config = {
      wrapSelector: '.home-collection-wrap',
      itemSelector: '.ml-item',
      excludeSelector: '.ml--ghost',
      paginationSelector: '.listings-pagination',
      pageSize: 6,
      revealClass: 'hidden',
      scrollTargetSelector: '.market-section',
      scrollOnPageChange: true,
      ...options
    };

    this.init();
  }

  init(): void {
    this.wrap = document.querySelector<HTMLElement>(this.config.wrapSelector);

    if (!this.wrap) {
      this.log('Wrapper not found, skipping initialization', this.config.wrapSelector);
      return;
    }

    const items = Array.from(
      this.wrap.querySelectorAll<HTMLElement>(this.config.itemSelector)
    ).filter((item) => !this.config.excludeSelector || !item.matches(this.config.excludeSelector));

    this.setItems(items);

    // Reveal only after page 1 is applied so the full list never flashes.
    this.wrap.classList.remove(this.config.revealClass);
    this.log('Collection revealed');
  }

  /**
   * Replace the paginated set (e.g. with the matching items from a filter),
   * rebuild the controls for the new page count, and reset to page 1 without
   * scrolling.
   *
   * Only items in the set are managed: items leaving it get their page-hide
   * class cleared and are otherwise left alone — hiding non-set items is the
   * caller's concern (e.g. IndexFilters' filter-hide class).
   */
  setItems(items: HTMLElement[]): void {
    if (!this.wrap) return;

    // Class hygiene: items leaving the set must not keep a stale page-hide
    this.items.forEach((item) =>
      item.classList.remove(IndexPagination.HIDDEN_ITEM_CLASS)
    );

    this.items = items;
    this.totalPages = Math.max(1, Math.ceil(this.items.length / this.config.pageSize));
    this.log(`Set ${this.items.length} item(s) -> ${this.totalPages} page(s)`);

    this.teardownControls();
    if (this.totalPages > 1) {
      this.paginationEl = this.resolvePaginationContainer();
      this.buildControls();
    }

    this.goToPage(1, { scroll: false });
  }

  /**
   * Show the given page (1-based, clamped to range) and update the controls.
   */
  goToPage(page: number, options: { scroll?: boolean } = {}): void {
    const { scroll = true } = options;
    this.currentPage = Math.min(Math.max(page, 1), this.totalPages);

    const start = (this.currentPage - 1) * this.config.pageSize;
    const end = start + this.config.pageSize;

    this.items.forEach((item, index) => {
      item.classList.toggle(
        IndexPagination.HIDDEN_ITEM_CLASS,
        index < start || index >= end
      );
    });

    this.updateControls();

    if (scroll && this.config.scrollOnPageChange) {
      this.scrollToTop();
    }

    this.log(`Showing page ${this.currentPage} of ${this.totalPages}`);
  }

  /**
   * Find the controls container, or create one after the wrapper so the
   * component still works before the Webflow export ships the dedicated div.
   */
  private resolvePaginationContainer(): HTMLElement {
    const existing = document.querySelector<HTMLElement>(this.config.paginationSelector);
    if (existing) return existing;

    this.log('Pagination container not found, creating one', this.config.paginationSelector);
    const container = document.createElement('div');
    const selector = this.config.paginationSelector;
    if (selector.startsWith('#')) {
      container.id = selector.slice(1);
    } else {
      container.className = selector.replace(/^\./, '');
    }
    this.wrap!.insertAdjacentElement('afterend', container);
    return container;
  }

  private buildControls(): void {
    if (!this.paginationEl) return;

    const nav = document.createElement('nav');
    nav.className = 'cp-nav';
    nav.setAttribute('aria-label', 'Listings pages');

    this.prevButton = this.createButton('Prev', 'cp-btn cp-btn--prev');
    this.prevButton.dataset.action = 'prev';
    this.prevButton.setAttribute('aria-label', 'Previous page');
    nav.appendChild(this.prevButton);

    this.pageButtons = [];
    for (let page = 1; page <= this.totalPages; page++) {
      const button = this.createButton(String(page), 'cp-btn cp-btn--page');
      button.dataset.page = String(page);
      button.setAttribute('aria-label', `Page ${page}`);
      this.pageButtons.push(button);
      nav.appendChild(button);
    }

    this.nextButton = this.createButton('Next', 'cp-btn cp-btn--next');
    this.nextButton.dataset.action = 'next';
    this.nextButton.setAttribute('aria-label', 'Next page');
    nav.appendChild(this.nextButton);

    this.clickHandler = (event: Event) => this.handleClick(event);
    nav.addEventListener('click', this.clickHandler);

    this.paginationEl.classList.add(IndexPagination.CONTAINER_CLASS);
    this.paginationEl.innerHTML = '';
    this.paginationEl.appendChild(nav);
    this.navEl = nav;
  }

  private handleClick(event: Event): void {
    const target = (event.target as HTMLElement).closest('button');
    if (!target || target.disabled) return;

    if (target.dataset.action === 'prev') {
      this.goToPage(this.currentPage - 1);
    } else if (target.dataset.action === 'next') {
      this.goToPage(this.currentPage + 1);
    } else if (target.dataset.page) {
      this.goToPage(Number(target.dataset.page));
    }
  }

  private updateControls(): void {
    if (!this.navEl) return;

    if (this.prevButton) this.prevButton.disabled = this.currentPage <= 1;
    if (this.nextButton) this.nextButton.disabled = this.currentPage >= this.totalPages;

    this.pageButtons.forEach((button) => {
      const isActive = Number(button.dataset.page) === this.currentPage;
      button.classList.toggle('is-active', isActive);
      if (isActive) {
        button.setAttribute('aria-current', 'page');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  private scrollToTop(): void {
    const target =
      document.querySelector<HTMLElement>(this.config.scrollTargetSelector) || this.wrap;

    // jsdom does not implement scrollIntoView
    if (!target || typeof target.scrollIntoView !== 'function') return;

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  private createButton(label: string, className: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = label;
    return button;
  }

  /** Remove the rendered controls and their listener */
  private teardownControls(): void {
    if (this.navEl && this.clickHandler) {
      this.navEl.removeEventListener('click', this.clickHandler);
    }
    this.clickHandler = null;

    if (this.paginationEl) {
      this.paginationEl.innerHTML = '';
      this.paginationEl.classList.remove(IndexPagination.CONTAINER_CLASS);
    }

    this.navEl = null;
    this.prevButton = null;
    this.nextButton = null;
    this.pageButtons = [];
  }

  /**
   * Cleanup method: removes controls and restores all items to visible.
   * The wrapper is intentionally left revealed.
   */
  destroy(): void {
    this.teardownControls();
    this.items.forEach((item) => item.classList.remove(IndexPagination.HIDDEN_ITEM_CLASS));
  }
}
